import React, {
  useCallback,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Spin } from 'antd';

import FormsTable from '../FormsTable';
import FormDetailView from '../CompletedForms/FormDetailView';

import { getFormById } from '../state/forms.actions';

import {
  getCustomers,
  getProjectIdMap,
  getUserIdMap,
  getVendors,
} from '../../state/selectors';
import { getFormListAndFilters } from '../formHelpers';
import { getFormsHomeLogColumns } from '../FormColumns';
import { FORMS_HOME_PREVIEW_LIMIT } from './formsHomeHelpers';

export default function FormsHomeLogSection({
  history,
  onCardColumnClick,
  range,
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const forms = useSelector((state) => state.forms.forms);
  const customers = useSelector(getCustomers);
  const userMap = useSelector(getUserIdMap);
  const projectIdMap = useSelector(getProjectIdMap);
  const vendors = useSelector(getVendors);
  const { settings = {} } = useSelector((state) => state.settings.company);

  const [showDetail, setShowDetail] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    formList,
    customerFilters,
    projectFilters,
    statusFilters,
  } = useMemo(() => getFormListAndFilters({
    forms: Object.values(forms),
    isPayable: false,
    userMap,
    vendorMap: vendors,
    projectIdMap,
    customers,
    hideDate: false,
    range,
  }), [forms, userMap, vendors, projectIdMap, customers, range]);

  const previewData = useMemo(
    () => formList
      .sort((a, b) => b.lastUpdated - a.lastUpdated)
      .slice(0, FORMS_HOME_PREVIEW_LIMIT),
    [formList],
  );

  const columns = useMemo(() => getFormsHomeLogColumns({
    projectIdMap,
    statusFilters,
    customerFilters,
    projectFilters,
    settings,
    t,
  }), [
    projectIdMap,
    statusFilters,
    customerFilters,
    projectFilters,
    settings,
    t,
  ]);

  const onFormClick = useCallback(async (record) => {
    if (loading) return;
    setLoading(true);
    if (await dispatch(getFormById(record.id, { getRecentDraft: true }))) {
      setShowDetail(true);
    }
    setLoading(false);
  }, [dispatch, loading]);

  return (
    <section className="forms-home-section">
      <div className="forms-home-section-header">
        <div>
          <h2 className="forms-home-section-title">Form log</h2>
          <p className="forms-home-section-subtitle">
            Your most recent submissions across every {t('Project').toLowerCase()}.
          </p>
        </div>
        <span
          className="forms-home-view-all"
          onClick={() => history.push('/forms/log')}
        >
          View all →
        </span>
      </div>
      <FormsTable
        data={previewData}
        columns={columns}
        onClickRow={onFormClick}
        isHomePreview
      />
      <FormDetailView
        visible={showDetail}
        onClose={() => setShowDetail(false)}
        userMap={userMap}
        onCardColumnClick={onCardColumnClick}
        zIndex={999}
      />
      {loading && (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin size="large" />
        </div>
      )}
    </section>
  );
}

FormsHomeLogSection.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  onCardColumnClick: PropTypes.func.isRequired,
  range: PropTypes.arrayOf(PropTypes.number),
};

FormsHomeLogSection.defaultProps = {
  range: null,
};
